import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Section, ghostButton, inputClass } from "@/components/settings/primitives";
import { useUpdateSettings } from "@/hooks/queries";
import { TIME_ZONES, browserTimeZone } from "@/lib/tasks";

export const switchClass = "data-[state=checked]:bg-white data-[state=unchecked]:bg-white/15 [&>span]:bg-ink-950 data-[state=unchecked]:[&>span]:bg-zinc-300";

export function Toggle({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span>
        <span className="block text-sm text-zinc-300">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-zinc-500">{hint}</span>}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} className={switchClass} />
    </div>
  );
}

export function TimezoneSection({ me }) {
  const update = useUpdateSettings();
  const qc = useQueryClient();
  const current = me.settings.timezone || "";
  const device = browserTimeZone();
  const options = !current || TIME_ZONES.includes(current) ? TIME_ZONES : [current, ...TIME_ZONES];
  const save = (timezone) =>
    update.mutate({ timezone }, {
      onSuccess: () => {
        ["tasks", "task-summary", "task-reminders"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
        toast.success(`Timezone set to ${timezone.replace(/_/g, " ")}`);
      },
      onError: (e) => toast.error(e.message),
    });
  return (
    <Section title="Timezone" description="Used for task dates, times, reminders and repeats." testId="settings-timezone-section">
      <select value={current} onChange={(e) => e.target.value && save(e.target.value)} className={`${inputClass} [color-scheme:dark]`} data-testid="settings-timezone-select">
        {!current && <option value="">Not set</option>}
        {options.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
      </select>
      {current !== device && (
        <button type="button" onClick={() => save(device)} className={`${ghostButton} mt-3 h-9 px-4 text-[13px]`} data-testid="settings-timezone-use-device">
          Use this device's timezone ({device.replace(/_/g, " ")})
        </button>
      )}
      <p className="mt-3 text-xs text-zinc-500">When you change it, tasks with a time stay at the same moment and all-day tasks stay on the same dates. Repeats follow your new timezone.</p>
    </Section>
  );
}
